import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { ActionItemsList, CustomerActionCard } from '@/features/purchase-orders/actions'
import { ActionItemSourceProvider } from '@/features/purchase-orders/action-source'
import { ACTION_FOCUS_CLASS, actionTargetProps, useActionFocus } from '@/hooks/use-action-focus'
import { useWarrantyAction, useWarrantyClaimActions, warrantyClaimKeys, type WarrantyClaim } from './api'
import { toPurchaseOrderAction, warrantySource } from './model'

/**
 * A claim's items on the PO action cards, under the claim (USA-915): the open ones, then those closed in
 * the last 30 days folded behind "Resolved & dismissed". Drawn flat, since the claim's row is the box.
 */
export function WarrantyClaimActions({
  claim,
  focusActionUuid,
  className,
}: {
  claim: WarrantyClaim
  /** The item the Action Items page opened: brought into view once its card is drawn. */
  focusActionUuid?: string
  className?: string
}) {
  const items = useWarrantyClaimActions(claim.uuid)
  const rows = items.data?.actions ?? []
  useActionFocus(rows.some((row) => row.uuid === focusActionUuid) ? focusActionUuid : undefined, !!items.data)
  const [showResolved, setShowResolved] = useState(false)
  // Most claims have no items: nothing is drawn, not even while loading.
  if (rows.length === 0) return null
  const actions = rows.map(toPurchaseOrderAction)
  return (
    <div className={cn('space-y-3 border-t pt-3', className)} aria-label="Claim action items" role="group">
    <ActionItemSourceProvider value={warrantySource(warrantyClaimKeys.claimActions(claim.uuid), rows, claim.po_uuid ?? null)}>
      <ActionItemsList
        poId=""
        flat
        openActions={actions.filter((action) => action.status === 'open')}
        resolvedActions={actions.filter((action) => action.status !== 'open')}
        reopens={[]}
        shares={[]}
        lineItems={[]}
        comments={{
          projectUuid: claim.project_uuid,
          poUuid: claim.po_uuid ?? undefined,
          poNumber: claim.po_number ?? undefined,
        }}
        showResolvedActions={showResolved}
        setShowResolvedActions={setShowResolved}
        showEmptyState={false}
      />
    </ActionItemSourceProvider>
    </div>
  )
}

/**
 * One warranty item no claim holds yet (warranty@ mail nobody could tie to a claim), opened from the Action
 * Items page on the same PO action card.
 */
export function WarrantyActionItem({ actionUuid }: { actionUuid: string }) {
  const item = useWarrantyAction(actionUuid)
  useActionFocus(actionUuid, !!item.data)
  if (item.isLoading) return <Skeleton className="h-40 w-full" />
  const found = item.data?.actions[0]
  if (!found) {
    return <p className="text-sm text-muted-foreground">This warranty item could not be loaded. Try again shortly.</p>
  }
  const action = toPurchaseOrderAction(found)
  return (
    <ActionItemSourceProvider value={warrantySource(warrantyClaimKeys.action(actionUuid), [found], null)}>
      <div {...actionTargetProps(action.uuid)} className={`rounded-xl border bg-card p-4 ${ACTION_FOCUS_CLASS}`}>
        <CustomerActionCard action={action} reopens={[]} />
      </div>
    </ActionItemSourceProvider>
  )
}
